import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  ShieldCheck, Download, UserPlus, MessageSquareWarning, Trash2, Loader2, Check,
  Scale, Lock, Mail, X, RotateCcw,
} from "lucide-react";
import { useApp } from "@/context/AppContext";
import { api, apiError, authToken } from "@/lib/api";
import { toast } from "sonner";

const Section = ({ icon: Icon, title, desc, children, testid }) => (
  <motion.div initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
    className="rud-card rounded-2xl p-6" data-testid={testid}>
    <div className="flex items-center gap-2.5">
      <div className="w-9 h-9 rounded-xl bg-brand/10 text-brand grid place-items-center shrink-0"><Icon size={18} /></div>
      <div>
        <h3 className="font-serif text-xl leading-tight">{title}</h3>
        {desc && <p className="text-sec text-[13px]">{desc}</p>}
      </div>
    </div>
    <div className="mt-4">{children}</div>
  </motion.div>
);

export default function DataRights() {
  const { user, setUser, country } = useApp();
  const isIndia = String(country || "").toUpperCase() === "IN";
  const [officer, setOfficer] = useState(null);
  const [nominee, setNominee] = useState({ name: "", email: "", relationship: "" });
  const [griev, setGriev] = useState({ category: "general", message: "" });
  const [grievances, setGrievances] = useState([]);
  const [erase, setErase] = useState({ pending: false, scheduled_at: null });
  const [busy, setBusy] = useState("");

  const consent = user?.consent || {};

  useEffect(() => {
    api.get(`/privacy/officer?country=${encodeURIComponent(country || "")}`).then(({ data }) => setOfficer(data)).catch(() => {});
    if (user) {
      api.get("/me/nominee").then(({ data }) => { if (data.nominee) setNominee(data.nominee); }).catch(() => {});
      api.get("/privacy/grievance/mine").then(({ data }) => setGrievances(data.grievances || [])).catch(() => {});
      api.get("/me/erase-status").then(({ data }) => setErase(data)).catch(() => {});
    }
  }, [user]);

  const toggleConsent = async (key) => {
    setBusy("consent");
    try {
      const payload = {
        privacy: true, terms: true,
        marketing: key === "marketing" ? !consent.marketing : !!consent.marketing,
        personalization: key === "personalization" ? !consent.personalization : !!consent.personalization,
        adult: true, country: consent.country || "",
      };
      const { data } = await api.post("/me/consent", payload);
      setUser((u) => (u ? { ...u, consent: data.consent } : u));
      toast.success("Consent updated");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(""); }
  };

  const exportData = async () => {
    setBusy("export");
    try {
      const base = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${base}/api/me/export-data`, { headers: { Authorization: `Bearer ${authToken.get()}` } });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = "my-kind-data.json"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Your data is downloading ✨");
    } catch { toast.error("Couldn't export. Try again."); } finally { setBusy(""); }
  };

  const saveNominee = async () => {
    setBusy("nominee");
    try { await api.post("/me/nominee", nominee); toast.success("Nominee saved"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(""); }
  };

  const submitGrievance = async () => {
    if (!griev.message.trim()) { toast.error("Please describe your grievance."); return; }
    setBusy("grievance");
    try {
      const { data } = await api.post("/privacy/grievance", griev);
      toast.success("Grievance recorded — we'll respond soon.");
      setGriev({ category: "general", message: "" });
      setGrievances((g) => [{ grievance_id: data.grievance_id, category: griev.category, message: griev.message, status: "open", created_at: new Date().toISOString() }, ...g]);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(""); }
  };

  const requestErase = async () => {
    if (!window.confirm("Schedule erasure of your account and personal data? You'll have 48 hours to cancel.")) return;
    setBusy("erase");
    try { const { data } = await api.post("/me/erase-request"); setErase({ pending: true, scheduled_at: data.scheduled_at }); toast.success("Erasure scheduled — cancel any time within 48h."); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(""); }
  };
  const cancelErase = async () => {
    setBusy("erase");
    try { await api.post("/me/erase-cancel"); setErase({ pending: false, scheduled_at: null }); toast.success("Erasure cancelled."); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(""); }
  };

  return (
    <div className="max-w-3xl mx-auto px-5 sm:px-8 py-12" data-testid="data-rights-page">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-brand"><ShieldCheck size={13} /> Privacy & Your Data</div>
      <h1 className="font-serif text-4xl sm:text-5xl tracking-tight mt-3">Your data, your rights.</h1>
      <p className="text-sec mt-3 max-w-2xl">{isIndia
        ? "Under India's Digital Personal Data Protection (DPDP) Act & Rules 2025, you're in control. Access, correct, export, withdraw consent, nominate, complain, or erase — all here."
        : "Under global privacy standards and your local data-protection law, you're in control. Access, correct, export, withdraw consent, nominate, complain, or erase — all here."}</p>

      {!user && <div className="rud-card rounded-2xl p-5 mt-6 text-sec text-sm">Please sign in to manage your data rights.</div>}

      {user && (
        <div className="space-y-4 mt-8">
          <Section icon={Scale} title="Manage your consent" desc="Withdraw is as easy as giving. Essentials keep the service running." testid="dr-consent">
            <div className="space-y-2">
              {[["personalization", "Personalisation", "Tailor content & suggestions to you."],
                ["marketing", "Product updates & offers", "Occasional emails about new features."]].map(([k, label, d]) => (
                <button key={k} data-testid={`dr-consent-${k}`} disabled={busy === "consent"} onClick={() => toggleConsent(k)}
                  className={`w-full text-left flex items-start gap-2.5 rounded-xl border p-3 transition-colors ${consent[k] ? "border-brand/40 bg-brand/5" : "border-rud"}`}>
                  <span className={`mt-0.5 w-4 h-4 rounded grid place-items-center shrink-0 ${consent[k] ? "bg-brand text-black" : "border border-sec"}`}>{consent[k] ? <Check size={12} /> : null}</span>
                  <span><span className="text-[13px] text-[var(--rud-text)] font-medium">{label}</span><span className="block text-[11px] text-sec">{d}</span></span>
                </button>
              ))}
              <div className="flex items-start gap-2.5 rounded-xl border border-rud p-3 opacity-80">
                <span className="mt-0.5 w-4 h-4 rounded grid place-items-center shrink-0 bg-brand/60 text-black"><Check size={12} /></span>
                <span><span className="text-[13px] text-[var(--rud-text)] font-medium">Essential (Terms & Privacy) <span className="text-[10px] text-brand ml-1">required</span></span><span className="block text-[11px] text-sec">Withdraw fully by erasing your account below.</span></span>
              </div>
            </div>
          </Section>

          <Section icon={Download} title="Access & export your data" desc="Download everything we hold about you (JSON)." testid="dr-export">
            <button data-testid="dr-export-btn" onClick={exportData} disabled={busy === "export"} className="btn-primary rounded-full h-10 px-5 text-sm flex items-center gap-2">
              {busy === "export" ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} Download my data
            </button>
          </Section>

          <Section icon={UserPlus} title="Nominate someone" desc="Nominate a person to exercise your rights on your behalf." testid="dr-nominee">
            <div className="grid sm:grid-cols-3 gap-2">
              <input data-testid="dr-nominee-name" value={nominee.name} onChange={(e) => setNominee({ ...nominee, name: e.target.value })} placeholder="Name" className="rud-input rounded-lg px-3 py-2 text-sm" />
              <input data-testid="dr-nominee-email" value={nominee.email} onChange={(e) => setNominee({ ...nominee, email: e.target.value })} placeholder="Email" className="rud-input rounded-lg px-3 py-2 text-sm" />
              <input data-testid="dr-nominee-rel" value={nominee.relationship} onChange={(e) => setNominee({ ...nominee, relationship: e.target.value })} placeholder="Relationship" className="rud-input rounded-lg px-3 py-2 text-sm" />
            </div>
            <button data-testid="dr-nominee-save" onClick={saveNominee} disabled={busy === "nominee"} className="btn-ghost rounded-full h-9 px-4 text-sm mt-3 flex items-center gap-2">
              {busy === "nominee" ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Save nominee
            </button>
          </Section>

          <Section icon={MessageSquareWarning} title="Raise a grievance" desc="Our Grievance Officer will respond to complaints about your data." testid="dr-grievance">
            <select data-testid="dr-grievance-cat" value={griev.category} onChange={(e) => setGriev({ ...griev, category: e.target.value })} className="rud-input rounded-lg px-3 py-2 text-sm w-full sm:w-56">
              <option value="general">General</option>
              <option value="consent">Consent</option>
              <option value="access">Access / Export</option>
              <option value="correction">Correction</option>
              <option value="erasure">Erasure</option>
            </select>
            <textarea data-testid="dr-grievance-msg" value={griev.message} onChange={(e) => setGriev({ ...griev, message: e.target.value })} rows={3} placeholder="Describe your grievance…" className="rud-input rounded-lg px-3 py-2 text-sm w-full mt-2" />
            <button data-testid="dr-grievance-submit" onClick={submitGrievance} disabled={busy === "grievance"} className="btn-primary rounded-full h-10 px-5 text-sm mt-3 flex items-center gap-2">
              {busy === "grievance" ? <Loader2 size={15} className="animate-spin" /> : <MessageSquareWarning size={15} />} Submit grievance
            </button>
            {grievances.length > 0 && (
              <div className="mt-4 space-y-2" data-testid="dr-grievance-list">
                {grievances.map((g) => (
                  <div key={g.grievance_id} className="text-[12px] rud-card rounded-lg p-2.5 flex items-center justify-between gap-2">
                    <span className="truncate"><b className="text-[var(--rud-text)]">{g.category}</b> — {g.message}</span>
                    <span className="text-brand uppercase text-[10px] shrink-0">{g.status}</span>
                  </div>
                ))}
              </div>
            )}
          </Section>

          <Section icon={Trash2} title="Erase your account" desc="Deletes your account & personal data. 48-hour cancellation window." testid="dr-erase">
            {erase.pending ? (
              <div className="rud-card rounded-xl p-3 border border-brand/30 bg-brand/5">
                <p className="text-sm text-[var(--rud-text)]">Erasure scheduled. Your data will be removed after the 48-hour window. Changed your mind?</p>
                <button data-testid="dr-erase-cancel" onClick={cancelErase} disabled={busy === "erase"} className="btn-primary rounded-full h-9 px-4 text-sm mt-3 flex items-center gap-2">
                  {busy === "erase" ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />} Cancel erasure
                </button>
              </div>
            ) : (
              <button data-testid="dr-erase-btn" onClick={requestErase} disabled={busy === "erase"} className="rounded-full h-10 px-5 text-sm flex items-center gap-2 border border-red-500/40 text-red-400 hover:bg-red-500/10 transition-colors">
                {busy === "erase" ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />} Erase my account
              </button>
            )}
          </Section>
        </div>
      )}

      {/* Officer + security — public */}
      <div className="grid sm:grid-cols-2 gap-4 mt-4">
        <Section icon={Mail} title="Grievance Officer" testid="dr-officer">
          {officer ? (
            <div className="text-sm text-sec space-y-1">
              <div className="text-[var(--rud-text)] font-medium">{officer.officer_name}</div>
              <a href={`mailto:${officer.officer_email}`} className="text-brand underline">{officer.officer_email}</a>
              <p className="text-[12px] mt-2">{officer.board_complaint}</p>
            </div>
          ) : <Loader2 size={16} className="animate-spin text-sec" />}
        </Section>
        <Section icon={Lock} title="How we protect your data" testid="dr-security">
          <ul className="text-[13px] text-sec list-disc pl-4 space-y-1">
            <li>Encryption in transit &amp; access controls</li>
            <li>Access logging &amp; monitoring</li>
            <li>Regular backups for resilience</li>
            <li>We never sell your data</li>
          </ul>
        </Section>
      </div>
    </div>
  );
}
